import type React from 'react';
import { useHistory } from 'react-router-dom';

import { Button, Divider, Heading, Icon, Paragraph, Text, Upsell } from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled, IconPiggyBankFilled } from '@zillow/constellation-icons';

import LeaseModal from '../../../../components/Containers/LeaseModal';
import type { Property } from '../../../../../shared/models';
import { Lease } from '../../../../../shared/models';
import { SetUpPaymentsLink } from '../../../../components/Widgets/SetUpPaymentsLink';

type Props = {
  lease: Lease;
  property: Property;
  isOpen: boolean;
  isColandlord: boolean;
  isRentGuaranteeEnabled?: boolean;
  onClose: () => void;
};

function CountersigningCompleteModal({ lease, isOpen, isColandlord, onClose, property }: Props): JSX.Element | null {
  const history = useHistory();

  function onGoToDashboard(): void {
    history.push(Lease.getDashboardPath());
  }

  function renderBody(): React.ReactNode {
    if (isColandlord) {
      return (
        <>
          <Icon size="lg" role="img" css={{ marginBottom: 'tight', color: 'icon.feedback.success.strong' }}>
            <IconCheckmarkCircleFilled />
          </Icon>
          <Heading
            level={2}
            css={{
              textStyle:
                'responsive.heading-xs' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              marginBottom: 'tight',
            }}
          >
            You’ve finished signing
          </Heading>
          <Paragraph>
            You’re one step closer to your tenant moving in. We’ll notify you once everyone signs so you can download
            your signed lease.
          </Paragraph>
        </>
      );
    } else if (!isColandlord) {
      return (
        <>
          <Icon size="lg" role="img" css={{ marginBottom: 'tight', color: 'icon.feedback.success.strong' }}>
            <IconCheckmarkCircleFilled />
          </Icon>
          <Heading
            level={2}
            css={{
              textStyle:
                'responsive.heading-xs' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              marginBottom: 'tight',
            }}
          >
            Signing is complete
          </Heading>
          <Paragraph>
            The lease will become active on its start date. You can view it anytime from your dashboard.
          </Paragraph>
          <Divider css={{ marginTop: 'tight', marginBottom: 'tight' }} />
          <Heading
            level={3}
            css={{
              textStyle:
                'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              marginBottom: 'tighter',
            }}
          >
            Next step
          </Heading>
          <Upsell
            header={<Heading level={4}>Collect move-in costs</Heading>}
            body={
              <Text>
                Request payment of your move-in costs (which include the security deposit, prorated rent, and any
                move-in fees) through Zillow Payments.
              </Text>
            }
            media={
              <Icon size="lg" role="img">
                <IconPiggyBankFilled />
              </Icon>
            }
            action={<SetUpPaymentsLink lease={lease} property={property} withMoveInCosts />}
            tone="soft"
            elevated={false}
            closeable={false}
          />
        </>
      );
    } else {
      return (
        <>
          <Icon size="lg" role="img" css={{ marginBottom: 'tight', color: 'icon.feedback.success.strong' }}>
            <IconCheckmarkCircleFilled />
          </Icon>
          <Heading
            level={2}
            css={{
              textStyle:
                'responsive.heading-xs' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              marginBottom: 'tight',
            }}
          >
            Signing is complete
          </Heading>
          <Paragraph>
            Your lease will become active on its start date. You can view it anytime from your dashboard.
          </Paragraph>
        </>
      );
    }
  }

  function renderFooter(): React.ReactNode {
    return (
      <Button tone="brand" emphasis="filled" onClick={onGoToDashboard}>
        Go to your dashboard
      </Button>
    );
  }

  if (!lease) {
    return null;
  }

  return (
    <LeaseModal
      header={<Heading level={1}>Lease signed</Heading>}
      isOpen={isOpen}
      onClose={onClose}
      footer={
        isColandlord ? (
          <Button onClick={onClose} tone="brand" emphasis="filled">
            OK
          </Button>
        ) : (
          renderFooter()
        )
      }
      body={<>{renderBody()}</>}
    />
  );
}

export default CountersigningCompleteModal;
